import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getBillingHistory, cancelSubscription, applyCredit, getInvoiceDownloadUrl } from '../services/billingService';
import { Download, X, DollarSign, Calendar, AlertCircle } from 'lucide-react';
import './BillingSection.css';

interface BillingSectionProps {
  familyId: string;
  stripeCustomerId?: string;
}

export default function BillingSection({ familyId, stripeCustomerId }: BillingSectionProps) {
  const queryClient = useQueryClient();
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [cancelImmediately, setCancelImmediately] = useState(false);
  const [creditAmount, setCreditAmount] = useState('');
  const [creditDescription, setCreditDescription] = useState('');

  const { data: billingHistory, isLoading } = useQuery({
    queryKey: ['billingHistory', familyId],
    queryFn: () => getBillingHistory(familyId),
    enabled: !!stripeCustomerId,
  });

  const cancelMutation = useMutation({
    mutationFn: (immediately: boolean) => cancelSubscription(familyId, immediately),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billingHistory', familyId] });
      queryClient.invalidateQueries({ queryKey: ['family', familyId] });
      queryClient.invalidateQueries({ queryKey: ['families'] });
      setShowCancelModal(false);
    },
  });

  const creditMutation = useMutation({
    mutationFn: () => applyCredit(familyId, parseFloat(creditAmount) * 100, 'usd', creditDescription),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billingHistory', familyId] });
      setShowCreditModal(false);
      setCreditAmount('');
      setCreditDescription('');
    },
  });

  const handleDownloadInvoice = async (invoiceId: string) => {
    try {
      const { invoicePdf, hostedInvoiceUrl } = await getInvoiceDownloadUrl(invoiceId);
      
      if (invoicePdf) {
        window.open(invoicePdf, '_blank');
      } else if (hostedInvoiceUrl) {
        window.open(hostedInvoiceUrl, '_blank');
      } else {
        alert('Invoice PDF not available');
      }
    } catch (error: any) {
      console.error('Error downloading invoice:', error);
      alert(error.message || 'Failed to download invoice');
    }
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (!stripeCustomerId) {
    return (
      <div className="billing-section">
        <div className="billing-header">
          <h2>Billing</h2>
        </div>
        <div className="no-billing">
          <p>No billing account set up for this family.</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="billing-section">
        <div className="billing-header">
          <h2>Billing</h2>
        </div>
        <div className="loading">Loading billing information...</div>
      </div>
    );
  }

  const activeSubscription = billingHistory?.subscriptions?.find(
    sub => sub.status === 'active' || sub.status === 'trialing'
  );

  return (
    <div className="billing-section">
      <div className="billing-header">
        <h2>Billing</h2>
        <div className="billing-actions">
          {activeSubscription && (
            <button
              className="danger-button"
              onClick={() => setShowCancelModal(true)}
            >
              <X size={18} />
              Cancel Subscription
            </button>
          )}
          <button
            className="primary-button"
            onClick={() => setShowCreditModal(true)}
          >
            <DollarSign size={18} />
            Apply Credit
          </button>
        </div>
      </div>

      {billingHistory?.upcomingInvoice && (
        <div className="upcoming-invoice-card">
          <div className="card-header">
            <Calendar size={20} />
            <h3>Next Bill</h3>
          </div>
          <div className="invoice-details">
            <div className="invoice-row">
              <span>Amount:</span>
              <strong>{formatCurrency(billingHistory.upcomingInvoice.amount, billingHistory.upcomingInvoice.currency)}</strong>
            </div>
            <div className="invoice-row">
              <span>Billing Period:</span>
              <span>
                {formatDate(billingHistory.upcomingInvoice.periodStart)} - {formatDate(billingHistory.upcomingInvoice.periodEnd)}
              </span>
            </div>
          </div>
        </div>
      )}

      {activeSubscription?.cancelAtPeriodEnd && (
        <div className="cancel-warning">
          <AlertCircle size={20} />
          <div>
            <strong>Subscription will cancel</strong>
            <p>This subscription is scheduled to cancel at the end of the current period ({formatDate(activeSubscription.currentPeriodEnd)}).</p>
          </div>
        </div>
      )}

      <div className="billing-history">
        <h3>Billing History</h3>
        {billingHistory?.invoices && billingHistory.invoices.length > 0 ? (
          <div className="invoices-list">
            {billingHistory.invoices.map((invoice) => (
              <div key={invoice.id} className="invoice-card">
                <div className="invoice-info">
                  <div>
                    <strong>Invoice {invoice.number || invoice.id.slice(-8)}</strong>
                    <p className="invoice-date">{formatDate(invoice.created)}</p>
                  </div>
                  <div className="invoice-amount">
                    <strong>{formatCurrency(invoice.amount, invoice.currency)}</strong>
                    <span className={`status-badge ${invoice.status}`}>{invoice.status}</span>
                  </div>
                </div>
                <button
                  className="download-button"
                  onClick={() => handleDownloadInvoice(invoice.id)}
                >
                  <Download size={16} />
                  Download
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state">No invoices found.</div>
        )}
      </div>

      {showCancelModal && (
        <div className="modal-overlay" onClick={() => setShowCancelModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Cancel Subscription</h2>
            <p>How would you like to cancel this subscription?</p>
            <div className="cancel-options">
              <label>
                <input
                  type="radio"
                  checked={!cancelImmediately}
                  onChange={() => setCancelImmediately(false)}
                />
                <div>
                  <strong>Cancel at period end</strong>
                  <p>Subscription will remain active until the end of the current billing period.</p>
                </div>
              </label>
              <label>
                <input
                  type="radio"
                  checked={cancelImmediately}
                  onChange={() => setCancelImmediately(true)}
                />
                <div>
                  <strong>Cancel immediately</strong>
                  <p>Subscription will be canceled right away. Access will be revoked immediately.</p>
                </div>
              </label>
            </div>
            <div className="modal-actions">
              <button className="secondary-button" onClick={() => setShowCancelModal(false)}>
                Cancel
              </button>
              <button
                className="danger-button"
                onClick={() => cancelMutation.mutate(cancelImmediately)}
                disabled={cancelMutation.isPending}
              >
                {cancelMutation.isPending ? 'Canceling...' : 'Confirm Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCreditModal && (
        <div className="modal-overlay" onClick={() => setShowCreditModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Apply Credit</h2>
            <div className="form-group">
              <label>Amount (USD)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={creditAmount}
                onChange={(e) => setCreditAmount(e.target.value)}
                placeholder="0.00"
              />
            </div>
            <div className="form-group">
              <label>Description (optional)</label>
              <textarea
                value={creditDescription}
                onChange={(e) => setCreditDescription(e.target.value)}
                placeholder="Reason for credit..."
                rows={3}
              />
            </div>
            <div className="modal-actions">
              <button className="secondary-button" onClick={() => setShowCreditModal(false)}>
                Cancel
              </button>
              <button
                className="primary-button"
                onClick={() => creditMutation.mutate()}
                disabled={!creditAmount || parseFloat(creditAmount) <= 0 || creditMutation.isPending}
              >
                {creditMutation.isPending ? 'Applying...' : 'Apply Credit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

